import type { MembershipSummary, TenantCapability } from '@alluzo/shared';
import { countryOfLocale } from '@alluzo/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { useMe } from '@/hooks/use-me';
import {
  adoptActiveTenant,
  resolveActiveTenant,
  switchActiveTenant,
} from '@/lib/active-tenant';
import { initialsOf } from '@/lib/business-name';
import { getActiveTenant, setActiveTenant } from '@/lib/entry-repo';
import { sync } from '@/lib/sync-engine';
import { usePerspectiveStore } from '@/stores/perspective-store';

export type ActiveTenant = {
  tenantId: string | null;
  tenantName: string | null;
  /** Null on most businesses. The header draws `tenantInitials` instead. */
  tenantLogoUrl: string | null;
  /** Two letters, one per word. Never derived twice on a screen. */
  tenantInitials: string | null;
  /** Every business this person belongs to, for the switcher sheet. */
  businesses: readonly MembershipSummary[];
  /**
   * Whether this person holds one tenant-scope capability in the active
   * business. Read off the membership /v1/me answered, so it is right offline
   * and never compares a rank on the phone. A courtesy: it decides which doors
   * are drawn, and the server refuses regardless.
   */
  holds: (capability: TenantCapability) => boolean;
  isLoading: boolean;
  /** True from the tap until the new business is the one every screen reads. */
  isSwitching: boolean;
  message: string | null;
  reload: () => void;
  switchTo: (tenantId: string) => Promise<void>;
};

const ActiveTenantContext = createContext<ActiveTenant | null>(null);

/** One array for every render where nobody has any, so memos hold still. */
const NO_MEMBERSHIPS: readonly MembershipSummary[] = [];

/**
 * The pick already on this device, read once, before the first frame.
 *
 * In the device database rather than AsyncStorage — `forgetDevice()` wipes it
 * with everything else, so the next account on this phone can never inherit
 * the last one's business. Read synchronously because it decides what the
 * first frame draws, and a promise here would flash the wrong business's name
 * on every launch. A database that cannot be opened yet answers null and the
 * resolution falls back to the first membership, which is what the app did
 * before any of this existed.
 */
const readPick = (): string | null => {
  try {
    return getActiveTenant();
  } catch {
    return null;
  }
};

/**
 * Which business the screens are looking at, and the one way to change it.
 *
 * A provider and not a plain hook, because the answer must be the same for
 * every screen in the same frame. Two screens each resolving it for themselves
 * would disagree for a render after a switch — the header on the new business
 * and the list still on the old — and the outbox would be pushed under
 * whichever one the sync manager happened to hold.
 *
 * The value is memoised. Every screen in the app reads this context, so a
 * value object rebuilt on each render would re-render all of them for nothing.
 */
export function ActiveTenantProvider({ children }: { children: ReactNode }): ReactElement {
  const { state, reload } = useMe();
  const [picked, setPicked] = useState<string | null>(readPick);
  const [isSwitching, setSwitching] = useState(false);

  const businesses = state.status === 'ready' ? state.me.memberships : NO_MEMBERSHIPS;

  /**
   * Resolved during render, not in an effect.
   *
   * An effect would give every launch one frame of "no business", which is the
   * frame home reads to decide between a spinner and a record list.
   *
   * Resolving against the memberships on every render is also what makes
   * sign-out safe. `forgetDevice()` wipes the stored pick, but this component
   * is not remounted, so `picked` still holds the last business in memory —
   * and it can reach nothing, because a pick that is not a membership of
   * whoever is signed in now is never honoured.
   */
  const tenantId = resolveActiveTenant(picked, businesses);

  /**
   * Write the fallback down when the stored pick did not survive.
   *
   * The provider is not the only reader of `active_tenant`: every repository
   * function falls back to it when a caller names no tenant. Leaving a ghost id
   * in the database would have the two disagreeing — see `adoptActiveTenant`.
   *
   * It writes and sets no state. What is on screen was already decided by the
   * line above, deterministically, from the same two facts this reads — so
   * setting `picked` here would be a second render for a value nothing would
   * read differently.
   */
  useEffect(() => {
    if (businesses.length === 0) return;
    try {
      adoptActiveTenant(businesses, { read: () => picked, write: setActiveTenant });
    } catch {
      // A database that cannot be written to is not a reason to take the app
      // down. The resolution made during render already stands for this
      // session, and the next launch tries again.
    }
  }, [businesses, picked]);

  const switchTo = useCallback(
    async (next: string): Promise<void> => {
      if (next === tenantId) return;
      // A perspective is pinned to the business it was picked in. Leaving that
      // business ends it, before the first screen of the next one is drawn.
      usePerspectiveStore.getState().exit();
      setSwitching(true);
      try {
        await switchActiveTenant(
          { next, previous: tenantId, memberships: businesses },
          { sync, setActive: setActiveTenant, onSwitched: setPicked },
        );
      } finally {
        setSwitching(false);
      }
    },
    [tenantId, businesses],
  );

  const value = useMemo((): ActiveTenant => {
    const membership = businesses.find((one) => one.tenantId === tenantId) ?? null;
    const name = membership?.tenantName ?? null;
    // A membership answered by a server older than 0007 carries no list, and a
    // missing list means nothing is held: a door that appears on an upgrade is
    // better than one that vanishes.
    const held = membership?.capabilities ?? [];
    return {
      tenantId,
      tenantName: name,
      tenantLogoUrl: membership?.tenantLogoUrl ?? null,
      tenantInitials: name === null ? null : initialsOf(name),
      businesses,
      holds: (capability) => held.includes(capability),
      isLoading: state.status === 'idle' || state.status === 'loading',
      isSwitching,
      message: state.status === 'failed' ? state.message : null,
      reload,
      switchTo,
    };
  }, [tenantId, businesses, state, isSwitching, reload, switchTo]);

  return <ActiveTenantContext.Provider value={value}>{children}</ActiveTenantContext.Provider>;
}

/**
 * Which business the screens are looking at.
 *
 * Every screen keys its reads off `tenantId`, so this is the one place the
 * question is answered — and since a switcher shipped, the one place it
 * changes. Throws outside the provider on purpose: a screen that resolved its
 * own business would be a second opinion.
 */
export const useActiveTenant = (): ActiveTenant => {
  const value = useContext(ActiveTenantContext);
  if (value === null) {
    throw new Error('useActiveTenant must be used inside ActiveTenantProvider.');
  }
  return value;
};

/**
 * The active business's own currency, or null outside the provider and before
 * the memberships load. A list card reads it for a money answer on a record
 * that carries no currency, without the card needing the whole tenant.
 */
export const useBusinessCurrency = (): string | null => {
  const value = useContext(ActiveTenantContext);
  if (value === null) return null;
  return value.businesses.find((one) => one.tenantId === value.tenantId)?.defaultCurrency ?? null;
};

/**
 * The active business's country, read off its locale (`en-IN` is IN), or null
 * outside the provider, before the memberships load, and for a membership a
 * server older than the field answered. A fresh phone field opens on it
 * (`PhoneCountryProvider`).
 */
export const useBusinessCountry = (): string | null => {
  const value = useContext(ActiveTenantContext);
  if (value === null) return null;
  return countryOfLocale(value.businesses.find((one) => one.tenantId === value.tenantId)?.locale);
};
